import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import qs from 'query-string';

import { withFirebase } from 'components/Firebase/index.js';
import * as ROUTES from 'constants/routes.js';
import { setOrUpdateAuthUser, setOrUpdateOrgId } from 'actions/session.js';
import { addFooterNotification } from 'actions/notifications.js';
import { withLocalization } from 'contexts/LocalizationContext.jsx';

const withAuthorization = condition => Component => {
  class WithAuthorization extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        isAuthorizing: true,
      };
      this._mounted = false;
    }

    componentDidMount() {
      this._mounted = true;
      const { firebase, match: { params: { orgId } }, setOrUpdateAuthUser, setOrUpdateOrgId, addNotification, localization } = this.props;
      const { labels, getErrorMessage } = localization;
      const {
        next,
        flow,
        state: externalAuthState,
      } = qs.parse(window.location.search);
      this.listener = this.props.firebase.onAuthUserListener(
        authUser => {
          setOrUpdateAuthUser(firebase.authUser);
          setOrUpdateOrgId(orgId);
          if (!this._mounted) return;
          if (!next) this.setState({ isAuthorizing: false });

          // No need for these re-directs unless uid changes
          if (this.uid === authUser.uid) return;
          this.uid = authUser.uid;

          if (!!next) {
            if (next.startsWith('https') && !!externalAuthState && flow === 'code') {
              firebase.createAuthCode().then(authCode => {
                window.location = `${next}?state=${externalAuthState}&code=${authCode}&uid=${authUser.uid}`;
              }).catch(error => {
                this.setState({ isAuthorizing: false });
                addNotification({
                  title: labels.authCodeFailedNotificationTitle,
                  message: getErrorMessage(error.code, error.message),
                  type: 'error',
                });
              });
              return;
            } else if (next.startsWith('/')) {
              this.props.history.push(next);
              return;
            }
          }

          // Only redirect from sign in or start trial pages
          if (this.props.location.pathname === ROUTES.SIGN_IN || this.props.location.pathname === ROUTES.START_TRIAL) {
            if (authUser.isSuperadmin) {
              this.props.history.push(ROUTES.GLOBAL);
            } else {
              this.props.history.push(ROUTES.LANDING);
            }
          }
        },
        () => {
          if (!this._mounted) return;
          this.setState({ isAuthorizing: false });
          if (this.props.location.pathname !== ROUTES.SIGN_IN) {
            this.props.history.push(ROUTES.SIGN_IN);
          }
        },
      );
    }

    componentWillUnmount() {
      this._mounted = false;
      this.listener();
    }

    render() {
      const { authUser, localization } = this.props;
      const { labels } = localization;
      const { isAuthorizing } = this.state;
      if (!condition(authUser)) return <p>{labels.accessDenied}</p>;
      const childProps = { ...this.props, isAuthorizing };
      return <Component {...childProps} />;
    }
  }

  const mapStateToProps = state => ({
    authUser: state.sessionState.authUser,
    currentOrgId: state.sessionState.currentOrgId,
  });

  const mapDispatchToProps = dispatch => ({
    setOrUpdateAuthUser: payload => dispatch(setOrUpdateAuthUser(payload)),
    setOrUpdateOrgId: payload => dispatch(setOrUpdateOrgId(payload)),
    addNotification: payload => {
      dispatch(addFooterNotification(payload));
    },
  });

  return compose(withRouter, withFirebase, withLocalization(getLabels), connect(mapStateToProps, mapDispatchToProps))(WithAuthorization);
};

const getLabels = (getText) => ({
  authCodeFailedNotificationTitle: getText('withAuthorizationHOC.authCodeFailedNotificationTitle'),
  accessDenied: getText('withAuthorizationHOC.accessDenied'),
});

export default withAuthorization;
